import React, { Reducer, useEffect, useReducer, useState } from 'react';
import { useManageContext } from '../../../../../contexts/ManageContext/ManageContext';
import {
  Dialog,
  ListItem,
  List,
  ListItemText,
  Checkbox,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  MenuItem,
  Divider,
  DialogTitle,
  Paper,
  TextField,
  IconButton,
  Menu,
} from '@material-ui/core';
import { useConfig } from '../../../../../eliot-components/src/ConfigProvider/hooks';
import { useAuthUser } from '../../../../../contexts/AuthProvider/hooks';
import {
  Org,
  Role,
  hasOneOfGlobalRoles,
  hasOneOfOrgRoles,
  hasOneOfRoles,
} from '../../../../../contexts/AuthProvider/types';
import { makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import {
  SortOption,
  SortOptionSelectItem,
  SortState,
  useSortState,
} from '../../../../../utils/sort';
import {
  orgsSortingReducer,
  mobileSortOptions,
  sortOrgs,
} from '../../provider';
import { IOrgMenuState, OrgsSortBy, SortableOrgs } from '../../types';
import MedSelect from '../../../../../eliot-components/src/Common/MedSelect';
import { Skeleton } from '@material-ui/lab';
import { Edit, MoreHoriz, RemoveCircleOutline } from '@material-ui/icons';
import { Action } from '../../../../../types';
import CreateOrgDialog from '../Org/CreateOrgDialog';
import DeleteOrgDialog from '../Org/DeleteOrgDialog';
import {
  useGetReportsQuery,
  useUpdateOrgMutation,
} from '../../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../../eliot-components/src/utils/helpers';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { isTotalMeasurementCount } from '../../../../../utils/helpers';

const useStyles = makeStyles((theme: Theme) => ({
  orgItem: {
    alignItems: 'flex-start',
    '& .org-checkbox': {
      padding: '12px 9px',
    },
    '& .org-name > span': {
      padding: '8px 0',
      display: 'inline-block',
    },
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  captionContainer: {
    display: 'flex',
    flexFlow: 'column wrap',
    justifyContent: 'flex-start',
    '& .edit': {
      position: 'absolute',
      bottom: '5px',
      right: '15px',
    },
  },
  editOrgName: {
    '& .MuiFormHelperText-root': {
      color: theme.palette.primary.main,
    },
  },
  caption: {
    marginRight: '5px',
    [theme.breakpoints.down('xs')]: {
      flex: '0 1 100%',
    },
  },
  selectButtons: {
    order: 1,
  },
  selectAll: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(0),
    margin: theme.spacing(1, 0),
    '& .select-all-labels': {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexGrow: 1,
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
  },
  list: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1, 1, 0, 1),
    overflow: 'auto',
    '& .item': {
      marginBottom: theme.spacing(1),
    },
  },
}));

export const menuStateReducer = (state: IOrgMenuState, action: Action) => {
  switch (action.type) {
    case 'OPEN_MENU':
      return {
        isMenuOpen: true,
        selectedOrg: action.data as string,
      };
    case 'EDIT_NAME':
      return {
        isMenuOpen: false,
        selectedOrg: action.data as string,
        isEditName: true,
      };
    case 'EDIT_CANCEL':
      return {
        isMenuOpen: false,
        selectedOrg: null,
        isEditName: false,
      };
    case 'CREATE_ORG':
      return {
        isMenuOpen: false,
        isCreateOrg: true,
      };
    case 'REMOVE_ORG':
      return {
        isMenuOpen: false,
        isDeleteOrg: true,
        selectedOrg: action.data as string,
      };
    case 'UNLINKED_SWITCH':
      return {
        isMenuOpen: false,
        isDeleteOrg: false,
        selectedOrg: action.data as string,
      };
    case 'CLOSE_MENU':
    default:
      return { isMenuOpen: false, selectedOrg: null };
  }
};

const SelectOrgDialog = () => {
  const config = useConfig();
  const { state, dispatch } = useManageContext();
  const { selectOrgs, authUser } = useAuthUser();
  const [selectedOrgs, setSelectedOrgs] = useState<Org[]>([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const [menuState, updateMenuState] = useReducer<
    Reducer<IOrgMenuState, Action>
  >(menuStateReducer, {
    isMenuOpen: false,
    selectedOrg: null,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [orgNameEdit, setOrgNameEdit] = useState<string>('');
  const { isOrgSelectionVisible, userOrgs: initialUserOrgs } = state;
  const [userOrgs, setUserOrgs] = useState<Org[]>(initialUserOrgs || []);
  const [sortBy, setSortBy] = useState<SortOption<OrgsSortBy> | null>(null);
  const [updateOrg, { isLoading: isLoadingUpdate }] = useUpdateOrgMutation();
  const css = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const { data: reports = [], isFetching: isLoading } = useGetReportsQuery({
    apiBaseURL: config.apiBaseURL,
    orgs: [],
    filters: [
      {
        category: 'archived',
        value: 'true',
      },
    ],
  });
  const allAreSelected = selectedOrgs.length === 0;

  useEffect(() => {
    if (initialUserOrgs && initialUserOrgs.length) {
      setUserOrgs(initialUserOrgs);
      if (authUser.selectedOrgs.length === 0) {
        setSelectedAll(true);
      }
    }
  }, [initialUserOrgs, authUser]);

  const measurementRealView = isTotalMeasurementCount();
  const onSortChange = (state: SortState<SortableOrgs>) => {
    if (userOrgs && reports) {
      const sortableOrgs = userOrgs.map((o) => {
        const report = reports.find((r) => r.owner === o.id);
        return {
          ...o,
          deviceCount: report ? report.deviceCount : 0,
          measurementCount: report
            ? measurementRealView
              ? report.measurementCount
              : report.measurementTripCount
            : 0,
        } as SortableOrgs;
      });
      const sortedOrgs = sortOrgs(sortableOrgs, state);
      setUserOrgs(sortedOrgs);
    }
  };

  const { updateSortState, currentOptionId } = useSortState(
    orgsSortingReducer,
    {},
    onSortChange,
    mobileSortOptions
  );

  useEffect(() => {
    if (sortBy !== null) {
      updateSortState(sortBy);
    }
  }, [sortBy, selectedOrgs, updateSortState]);

  useEffect(() => {
    if (state.selectedOrgs) {
      setSelectedOrgs(state.selectedOrgs);
    }
  }, [state.selectedOrgs]);

  const maximumOrgsMessage =
    'Maximum selected devices or organizations is reached, some parts of UI will be disabled';

  const handleOrgMenuClick = (org: Org) => {
    let orgs;
    if (selectedOrgs.some((o) => o.id === org.id)) {
      orgs = selectedOrgs.filter((o) => o.id !== org.id);
    } else {
      orgs = [...selectedOrgs, { ...org }];
    }

    const deviceNum = orgs
      .map((org1) => {
        const report = reports.find((r) => r.owner === org1.id);
        return report ? report.deviceCount : 0;
      })
      .reduce((a, b) => a + b, 0);

    setSelectedAll(false);
    setSelectedOrgs(orgs);

    if (deviceNum > 8000 || orgs.length > 99) {
      enqueueSnackbar(maximumOrgsMessage, {
        variant: 'warning',
      });
    }
  };

  const deviceNum = selectedOrgs
    .map((org1) => {
      const report = reports.find((r) => r.owner === org1.id);
      return report ? report.deviceCount : 0;
    })
    .reduce((a, b) => a + b, 0);

  const handleSingleSelect = (org: Org) => {
    setSelectedAll(false);
    setSelectedOrgs([org]);
    selectOrgs([org.id]);
    redirectToOrg();
    redirectToTS();
  };

  const handleSubmit = () => {
    selectOrgs(allAreSelected ? [] : selectedOrgs.map((o) => o.id));
    redirectToOrg();
    redirectToTS();
  };

  const redirectToOrg = () => {
    if (/^\/devices?.+/.test(location.pathname)) {
      history.replace('/devices');
      history.push('/devices'); // Navigate to /devices if user is on device details page
    }
  };

  const redirectToTS = () => {
    if (/^\/webhooks?.+/.test(location.pathname)) {
      history.replace('/webhooks');
      history.push('/webhooks'); // Navigate to /webhooks if user is on target systems  page
    }
  };

  if (userOrgs) {
    const getListItem = (keyName: string) => (org: Org, index: number) => {
      const report = reports.find((r) => r.owner === org.id);
      return (
        <Paper elevation={1} className="item" key={keyName + index}>
          <ListItem
            disabled={org.deactivated}
            data-testid="org-list-item"
            button
            onClick={() => {
              if (!org.deactivated) {
                handleSingleSelect(org);
              }
            }}
            className={css.orgItem}
          >
            <Checkbox
              role="multiselect-checkbox"
              className="org-checkbox"
              checked={selectedOrgs.some((o) => o.id === org.id) || selectedAll}
              onClick={(event) => {
                handleOrgMenuClick(org);
                event.stopPropagation();
              }}
            />
            <ListItemText>
              <div className="org-name">
                {menuState.isEditName && menuState.selectedOrg === org.id ? (
                  <div>
                    <TextField
                      required
                      variant="outlined"
                      margin="none"
                      size="small"
                      label="Organization name"
                      value={orgNameEdit}
                      disabled={isLoadingUpdate}
                      className={css.editOrgName}
                      inputProps={{ maxLength: 60 }}
                      helperText={
                        orgNameEdit && orgNameEdit.length >= 60
                          ? 'Maximum character length reached'
                          : null
                      }
                      onChange={(e) => {
                        setOrgNameEdit(e.target.value);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    />
                    <Button
                      color={'primary'}
                      disabled={isLoadingUpdate}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (orgNameEdit.length === 0)
                          enqueueSnackbar(
                            'Minimum character length is 1 character',
                            { variant: 'warning' }
                          );
                        if (
                          orgNameEdit &&
                          orgNameEdit.length < 60 &&
                          orgNameEdit.length > 0
                        ) {
                          updateOrg({
                            org: { id: org.id, name: orgNameEdit },
                            apiBaseURL: config.apiBaseURL,
                          }).then((resp: any) => {
                            if (!resp.error) {
                              let orgs = userOrgs.map((o: Org) => {
                                let clone = { ...o };
                                if (o.id === org.id) {
                                  clone.name = orgNameEdit;
                                }
                                return clone;
                              });
                              setUserOrgs(orgs);
                              updateMenuState({ type: 'EDIT_CANCEL' });
                              enqueueSnackbar('Organization name updated', {
                                variant: 'info',
                              });

                              return;
                            }
                            enqueueSnackbar(parseErrorMessage(resp.error), {
                              variant: 'error',
                            });
                          });
                        }
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      color={'primary'}
                      disabled={isLoadingUpdate}
                      onClick={(e) => {
                        e.stopPropagation();
                        updateMenuState({ type: 'EDIT_CANCEL' });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  org.name
                )}
              </div>
              <Typography variant="caption">
                {!isLoading && !isLoadingUpdate ? (
                  <div className={css.captionContainer}>
                    <span className={css.caption}>
                      <b>Devices:</b>{' '}
                      {report ? report.deviceCount.toLocaleString() : 0}
                    </span>
                    <span className={css.caption}>
                      <b>Measurements:</b>{' '}
                      {report
                        ? measurementRealView
                          ? report.measurementCount.toLocaleString()
                          : report.measurementTripCount.toLocaleString()
                        : 0}
                    </span>

                    <div
                      style={{ position: 'relative', pointerEvents: 'visible' }}
                    >
                      <IconButton
                        color={'primary'}
                        className="edit"
                        aria-controls="org-menu"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                          updateMenuState({
                            type: 'OPEN_MENU',
                            data: org.id,
                          });
                        }}
                      >
                        <MoreHoriz />
                      </IconButton>
                    </div>
                    <Menu
                      id="org-menu"
                      open={
                        menuState.isMenuOpen && menuState.selectedOrg === org.id
                      }
                      anchorEl={anchorEl}
                      variant="selectedMenu"
                      onClose={() => {
                        setAnchorEl(null);
                        updateMenuState({ type: 'CLOSE_MENU' });
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <MenuItem
                        disabled={
                          !hasOneOfOrgRoles(
                            authUser,
                            [org.id],
                            [Role.OrganizationManager]
                          ) || org.deactivated
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMenuState({
                            type: 'EDIT_NAME',
                            data: org.id,
                          });
                          setOrgNameEdit(org.name);
                        }}
                      >
                        <Edit className={css.icon} />
                        Edit Name
                      </MenuItem>
                      <MenuItem
                        disabled={
                          !hasOneOfGlobalRoles(authUser, [
                            Role.OrganizationManager,
                          ]) || org.deactivated
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMenuState({
                            type: 'REMOVE_ORG',
                            data: org.id,
                          });
                        }}
                      >
                        <RemoveCircleOutline className={css.icon} />
                        Remove Org
                      </MenuItem>

                      <MenuItem
                        disabled={
                          !hasOneOfOrgRoles(
                            authUser,
                            [org.id],
                            [Role.OrganizationManager]
                          )
                        }
                      >
                        <FormControl component="fieldset">
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  color="primary"
                                  checked={org.deactivated}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setAnchorEl(null);
                                    updateMenuState({ type: 'CLOSE_MENU' });

                                    const isChecked = e.target.checked;
                                    updateOrg({
                                      org: {
                                        id: org.id,
                                        name: org.name,
                                        deactivated: isChecked,
                                      },
                                      apiBaseURL: config.apiBaseURL,
                                    }).then((resp: any) => {
                                      if (!resp.error) {
                                        enqueueSnackbar(
                                          `Organization ${
                                            isChecked
                                              ? 'deactivated'
                                              : 'activated'
                                          }`,
                                          {
                                            variant: 'info',
                                          }
                                        );
                                        return;
                                      }
                                      enqueueSnackbar(
                                        parseErrorMessage(resp.error),
                                        {
                                          variant: 'error',
                                        }
                                      );
                                    });
                                  }}
                                />
                              }
                              label="Deactivated"
                            />
                          </FormGroup>
                        </FormControl>
                      </MenuItem>
                    </Menu>
                  </div>
                ) : (
                  <div className={css.captionContainer}>
                    <Skeleton animation={false} variant="text" width="10ch" />
                    <Skeleton animation={false} variant="text" width="15ch" />
                  </div>
                )}
              </Typography>
            </ListItemText>
          </ListItem>
        </Paper>
      );
    };
    const selectedOrgsList = userOrgs
      .filter(
        (x) =>
          state.selectedOrgs && state.selectedOrgs.some((o) => o.id === x.id)
      )
      //.filter((x) => !x.deactivated)
      .map(getListItem('selected'));

    const restOrgsList = userOrgs
      .filter(
        (x) =>
          !state.selectedOrgs || !state.selectedOrgs.some((o) => o.id === x.id)
      )
      //.filter((x) => !x.deactivated)
      .map(getListItem('notselected'));

    const handleClose = () => {
      setSelectedOrgs(state.selectedOrgs || []);
      dispatch({ type: 'org-selection', data: { visible: false } });
    };

    const onSelectAllChanged = () => {
      if (selectedAll) {
        setSelectedAll(false);
      } else {
        setSelectedOrgs([]);
        setSelectedAll(true);
        const deviceNum = reports
          .map((report) => {
            return report.deviceCount ? report.deviceCount : 0;
          })
          .reduce((a, b) => a + b, 0);
        if (deviceNum > 8000 || authUser.orgs.length > 99) {
          enqueueSnackbar(maximumOrgsMessage, {
            variant: 'warning',
          });
        }
      }
    };
    const someAreSelected = selectedOrgs.length > 0 && !allAreSelected;

    const orgCount = userOrgs.length;
    const selectedTextCount =
      selectedOrgs && selectedOrgs.length === 0
        ? selectedAll
          ? orgCount
          : 0
        : selectedOrgs.length || 0;

    return (
      <Dialog onClose={handleClose} open={isOrgSelectionVisible === true}>
        <DialogTitle>
          <MedSelect
            disabled={isLoading}
            name="sort-option"
            placeholder="Sort by"
            variant="outlined"
            fullWidth
            options={mobileSortOptions}
            valueGetter={(o: SortOptionSelectItem<OrgsSortBy>) => o.id}
            optionTextGetter={(o: SortOptionSelectItem<OrgsSortBy>) => o.label}
            value={currentOptionId}
            onChange={(e) => {
              const option = mobileSortOptions.find(
                (o) => o.id === e.target.value
              );
              option && setSortBy(option.value);
            }}
          />
          <div style={{ display: 'flex', padding: '0 10px' }}>
            <MenuItem className={css.selectAll} onClick={onSelectAllChanged}>
              <Checkbox
                indeterminate={someAreSelected}
                checked={selectedAll}
                style={{ paddingLeft: 0 }}
              />
              <div className="select-all-labels">
                <Typography variant="body1">Select all</Typography>
              </div>
            </MenuItem>
            <Typography
              variant="caption"
              style={{
                flex: 1,
                justifyContent: 'left',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {`Selected Orgs ${selectedTextCount}/${orgCount} `} &nbsp;
              {` Devices ${(selectedAll
                ? reports.map((r) => r.deviceCount).reduce((a, b) => a + b, 0)
                : deviceNum
              ).toLocaleString()}/${reports
                .map((r) => r.deviceCount)
                .reduce((a, b) => a + b, 0)
                .toLocaleString()}`}
            </Typography>
            <span style={{ textAlign: 'right', display: 'flex' }}>
              <Button
                disabled={!hasOneOfRoles(authUser, [Role.OrganizationManager])}
                color="primary"
                onClick={() => updateMenuState({ type: 'CREATE_ORG' })}
              >
                Create org
              </Button>
            </span>
          </div>
        </DialogTitle>
        <DialogContent>
          <List className={css.list}>{selectedOrgsList}</List>
          <Divider />
          <List className={css.list}>{restOrgsList}</List>
        </DialogContent>
        <CreateOrgDialog
          onCreated={() => {
            updateMenuState({ type: 'CLOSE_MENU' });
          }}
          onClose={() => {
            updateMenuState({ type: 'CLOSE_MENU' });
          }}
          isOpen={!!menuState.isCreateOrg}
        />
        <DeleteOrgDialog
          orgId={menuState.selectedOrg || ''}
          isOpen={!!menuState.isDeleteOrg}
          onCreated={() => {
            updateMenuState({ type: 'CLOSE_MENU' });
          }}
          onClose={() => {
            updateMenuState({ type: 'CLOSE_MENU' });
          }}
        />
        <DialogActions>
          <Grid
            item
            container
            justifyContent="space-between"
            direction="row-reverse"
            wrap="wrap-reverse"
          >
            <Grid item>
              <Button color="primary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                disabled={selectedOrgs.length === 0 && !selectedAll}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
};

export default SelectOrgDialog;
